import { queryStatus } from 'utils/queryStatus';
import { sortCancelledExperiences } from 'utils/mappers/sorting';
import useGetOrganisationGroups from 'components/hooks/useGetOrganisationGroups';
import { useMemo } from 'react';
import { isGroupManager, isSuperAdmin } from 'utils/role';
import {
  EventSummaryResponse,
  Group,
  GroupResponse,
  OrganisationResponse,
  PractitionerData,
  WalletResponse,
  useGetCancelledPractitionerEvents,
  useGetOrganisation,
  useGetOrgsWithRevokeAccessFlag,
  useGetPractitionerById,
  useGetPractitionerEvents,
  useGetPractitionerOrganisations,
  useGetPractitionerWallet,
} from 'api';
import { QueryStatus } from '@tanstack/react-query';
import { useAuth } from 'contexts/AuthContext';

const useGetAccountData = (
  practitionerId: string,
  organisationId: string,
): {
  status: QueryStatus;
  data: {
    practitioner: PractitionerData | undefined;
    wallet: WalletResponse | undefined;
    organisation: OrganisationResponse | undefined;
    groups: GroupResponse[];
    activeEvents: EventSummaryResponse[] | undefined;
    cancelledEvents: EventSummaryResponse[];
    practitionerOrganisations: OrganisationResponse[];
    organisationsInGroups: { [id: string]: Group };
  };
} => {
  const { user } = useAuth();

  const { status: getPractitionerStatus, data: practitioner } =
    useGetPractitionerById(practitionerId);
  const { status: getWalletStatus, data: wallet } =
    useGetPractitionerWallet(practitionerId);
  const { status: getOrganisationStatus, data: organisation } =
    useGetOrganisation(organisationId);
  const { status: activeEventsStatus, data: activeEvents } =
    useGetPractitionerEvents(practitionerId);
  const { status: cancelledEventsStatus, data: cancelledEvents } =
    useGetCancelledPractitionerEvents(practitionerId);

  const shouldFetchOrgsWithRevokeAccess =
    isSuperAdmin(user?.role) && isSuperAdmin(practitioner?.role);
  const { status: organisationsStatus, data: orgs } =
    useGetPractitionerOrganisations(practitioner?.id, {
      enabled: !!practitioner?.id && !shouldFetchOrgsWithRevokeAccess,
    });
  const { data: revokeAccessOrgs, status: fetchOrgWithRevokeFlagStatus } =
    useGetOrgsWithRevokeAccessFlag(practitioner?.id || '', {
      enabled: !!practitioner?.id && shouldFetchOrgsWithRevokeAccess,
    });

  const practitionerOrganisations =
    shouldFetchOrgsWithRevokeAccess || !orgs ? revokeAccessOrgs : orgs;
  const {
    status: organisationGroupStatus,
    data: organisationsInGroups,
    groups,
  } = useGetOrganisationGroups({
    practitionerId,
    enabled: true,
    organisations: practitionerOrganisations,
  });

  const practitionerOrganisationsStatus = shouldFetchOrgsWithRevokeAccess
    ? fetchOrgWithRevokeFlagStatus
    : organisationsStatus;

  const status = queryStatus(
    getPractitionerStatus,
    getWalletStatus,
    getOrganisationStatus,
    activeEventsStatus,
    practitionerOrganisationsStatus,
    cancelledEventsStatus,
    organisationGroupStatus,
  );

  const availableOrganisations: OrganisationResponse[] = useMemo(() => {
    if (
      Object.keys(organisationsInGroups).length === 0 ||
      !isGroupManager(practitioner?.role)
    ) {
      return practitionerOrganisations ?? [];
    }
    return (
      practitionerOrganisations?.filter(
        ({ id }) => !organisationsInGroups[id],
      ) ?? []
    );
  }, [organisationsInGroups, practitioner?.role, practitionerOrganisations]);

  return {
    status,
    data: {
      practitioner,
      wallet,
      organisation,
      groups,
      activeEvents,
      cancelledEvents: sortCancelledExperiences(cancelledEvents),
      practitionerOrganisations: availableOrganisations,
      organisationsInGroups,
    },
  };
};

export default useGetAccountData;
